import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  CargoSearchListApiResponse, 
  getListaCargoSearchList, 
  getCargoSearchListById, 
  createCargoSearchList, 
  updateCargoSearchList, 
  deleteCargoSearchList, 
  getCargoSearchListPaginated,
  getListaCargoSearch
} from "../../services/cargoSearchListService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import CargoSearchList from "../../types/cargoSearchList";
import { ApiResponse } from "../../services/api";
import Paginacao from "../../types/paginacao";

interface CargoSearchListContextProps {
  apiResponse: ApiResponse<CargoSearchList> | null;
  cargosSearchList: CargoSearchList[] | null;
  cargoSearchListById: CargoSearchListApiResponse | null;
  loading: boolean;
  error: string | null;
  fetchCargoSearchList: () => void;
  fetchCargoSearchListPaginated: (paginacao: Paginacao | null) => void;
  fetchCargoSearchListById: (id: number) => Promise<CargoSearchListApiResponse | null>;
  postCargoSearchList: (cargo: CargoSearchList) => Promise<CargoSearchListApiResponse | null>;
  putCargoSearchList: (id: number, cargo: CargoSearchList) => Promise<CargoSearchListApiResponse | null>;
  excluiCargoSearchList: (id: number) => Promise<boolean>;
}

interface CargoSearchListProviderProps {
  children: React.ReactNode;
}

const CargoSearchListContext = createContext<CargoSearchListContextProps | null>(null);

const CargoSearchListProvider: React.FC<CargoSearchListProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<CargoSearchList> | null>(null);
  const [cargosSearchList, setCargoSearchList] = useState<CargoSearchList[] | null>(null);
  const [cargoSearchListById, setCargoSearchListById] = useState<CargoSearchListApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCargoSearchList = useCallback(async () => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaCargoSearchList();
      setCargoSearchList(response.values || []);
    } catch (err) {
      setError(getErrorMessage("fetchCargoSearchListError"));
    } finally {
      setLoading(false);
    }
  }, []);

    const fetchCargoSearchListPaginated = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    try {
      setLoading(true);
      const response = (paginacao) ? await getCargoSearchListPaginated(paginacao) : await getListaCargoSearch();
      setApiResponse(response);
      setCargoSearchList(response.data.values || []);
    } catch (err) {
      setCargoSearchList([]);
      setError(getErrorMessage("fetchCargoSearchListError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCargoSearchListById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getCargoSearchListById(id);
      setCargoSearchListById(response);
      return response;
    } catch (err) {
      setError(getErrorMessage("fetchCargoSearchListByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postCargoSearchList = useCallback(async (cargo: CargoSearchList) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createCargoSearchList(cargo);
      fetchCargoSearchList();
      return response;
    } catch (err) {
      setError(getErrorMessage("createCargoSearchListError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchCargoSearchList]);

  const putCargoSearchList = useCallback(async (id: number, cargo: CargoSearchList) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateCargoSearchList(id, cargo);
      fetchCargoSearchList();
      return response;
    } catch (err) {
      setError(getErrorMessage("updateCargoSearchListError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchCargoSearchList]);

  const excluiCargoSearchList = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteCargoSearchList(id);
      fetchCargoSearchList();
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteCargoSearchListError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchCargoSearchList]);

  return (
    <CargoSearchListContext.Provider 
      value={{ 
        apiResponse,
        cargosSearchList, 
        cargoSearchListById, 
        loading, 
        error, 
        fetchCargoSearchList, 
        fetchCargoSearchListPaginated,
        fetchCargoSearchListById, 
        postCargoSearchList, 
        putCargoSearchList, 
        excluiCargoSearchList 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </CargoSearchListContext.Provider>
  );
};

export default CargoSearchListProvider;

export const useCargoSearchList = (): CargoSearchListContextProps => {
  const context = useContext(CargoSearchListContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useCargoSearchList?.name,
        providerName: CargoSearchListProvider?.name,
      })
    );
  }
  return context;
};
